import { Button, toast } from "@/components/ui";
import { isActionAvailable } from "@/utils/available-actions";
import { useRef, useState } from "react";
import type { z } from "zod";

import {
  CareplanAvailableAction,
  useCarePlanSummary,
} from "../network/useCarePlanSummary";
import { useCreateFollowUp } from "../network/useCreateFollowUp";
import { FollowUpEditor } from "./FollowUpEditor";
import type { editFollowUpSchema } from "./schemas";

interface AddFollowUpProps {
  carePlanId: string;
  childEncounterId: string;
}

export const AddFollowUp = ({
  carePlanId,
  childEncounterId,
}: AddFollowUpProps) => {
  const [isAddingFollowUp, setIsAddingFollowUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const createFollowUp = useCreateFollowUp({ childEncounterId, carePlanId });

  const { data: careplanSummary } = useCarePlanSummary(childEncounterId);
  const careplanAvailableActions = careplanSummary?.availableActions;

  const canWriteFollowUps = isActionAvailable(
    CareplanAvailableAction.ADD_FOLLOW_UP,
    careplanAvailableActions,
  );

  const handleClick = () => {
    setIsAddingFollowUp(true);
  };

  const onSubmit = async (values: z.infer<typeof editFollowUpSchema>) => {
    try {
      await createFollowUp.mutateAsync({
        followupMessage: values.text,
        followupDurationValue: values.total,
        followupDurationUnit: values.unit,
      });

      setIsAddingFollowUp(false);
    } catch (error) {
      toast.error("Failed to add follow up.");
    }
  };

  if (isAddingFollowUp)
    return (
      <div className="border rounded-md px-3 py-2" ref={ref}>
        <FollowUpEditor
          onSubmit={onSubmit}
          onCancel={() => setIsAddingFollowUp(false)}
          isLoading={createFollowUp.isPending}
        />
      </div>
    );

  return (
    <Button
      variant="secondary"
      size="xs"
      onClick={handleClick}
      className="w-fit mt-2"
      disabled={!canWriteFollowUps}
    >
      Add Follow Up
    </Button>
  );
};
